import { useCallback } from 'react';

import type { QueryResult } from '@apollo/client';
import type { UpdateQueryFn } from '@apollo/client/core/watchQueryOptions';
import produce from 'immer';
import { maxBy } from 'lodash';

import type { SubscriptionDescriptor } from './ReactiveSource';
import useEntitySubscriptions from './useEntitySubscriptions';

const DEFAULT_UPDATE_SUBSCRIPTION_NAME = 'update';

export type UseUpdateSubscriptionsInput<EntityResponse, QueryResponse> = {
  subscription?: SubscriptionDescriptor;
  queryResult: QueryResult<QueryResponse>;
  selectEntityId: (entity: EntityResponse) => string;
  selectEntities: (resp?: QueryResponse) => EntityResponse[];
  onSubscribe: (entity: EntityResponse) => void;
  selectSyncOn: (entity: EntityResponse) => Date;
};

export default function useUpdateSubscriptions<EntityResponse, QueryResponse, SubscriptionResponse>({
  subscription,
  queryResult,
  onSubscribe,
  selectEntities,
  selectEntityId,
  selectSyncOn,
}: UseUpdateSubscriptionsInput<EntityResponse, QueryResponse>) {
  const { subscribe: utilSubscribe, unsubscribeFrom } = useEntitySubscriptions();

  const updateQuery: UpdateQueryFn<QueryResponse, unknown, SubscriptionResponse> = useCallback(
    (prevState: QueryResponse, { subscriptionData }) => {
      if (!subscriptionData.data || !prevState) {
        return prevState;
      }

      const updatedEntity = subscriptionData.data[
        subscription!.name ?? DEFAULT_UPDATE_SUBSCRIPTION_NAME
      ] as EntityResponse;

      return produce(prevState, draftState => {
        const entities = selectEntities(draftState as QueryResponse);

        // finds updated entity in query result from Apollo cache
        const updatedEntityIndex = entities.findIndex(e => selectEntityId(e) === selectEntityId(updatedEntity));

        // executes selectSyncOn function on updated entity both from subscription and query result + compares their values
        // when entity is Task, selectSyncOn function will extract updatedAt field, so query result will contain latest entity(task) status
        if (updatedEntityIndex !== -1) {
          entities[updatedEntityIndex] = maxBy([updatedEntity, entities[updatedEntityIndex]], selectSyncOn)!;
        }
      });
    },
    [subscription, selectEntities, selectEntityId, selectSyncOn]
  );

  const subscribe = useCallback(
    (entity: EntityResponse) => {
      const entityId = selectEntityId(entity);

      if (subscription) {
        utilSubscribe(
          entityId,
          queryResult.subscribeToMore({
            document: subscription.query,
            variables: {
              id: entityId,
            },
            updateQuery,
          })
        );

        onSubscribe(entity);
      }
    },
    [utilSubscribe, updateQuery, selectEntityId, queryResult, subscription, onSubscribe]
  );

  return { subscribe, unsubscribeFrom };
}
